import React from 'react';
import { CheckBadgeIcon, ClockIcon, XCircleIcon, ArchiveBoxIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import type { ShopTrustLabel } from '../../types/buildExpansion';

const LABELS: Record<ShopTrustLabel, string> = {
  verified: 'Verified shop',
  community_verified: 'Community verified',
  pending_review: 'Pending review',
  archived: 'Archived',
  closed: 'Closed',
  rejected: 'Rejected',
  listed: 'Listed',
};

const STYLES: Record<ShopTrustLabel, string> = {
  verified: 'badge-brand',
  community_verified: 'bg-emerald-950/50 text-emerald-300 border border-emerald-800',
  pending_review: 'bg-amber-950/50 text-amber-300 border border-amber-800',
  archived: 'bg-[#111] text-gray-400 border border-white/[0.08]',
  closed: 'bg-[#111] text-gray-500 border border-white/[0.08]',
  rejected: 'bg-red-950/50 text-red-300 border border-red-900',
  listed: 'bg-[#111] text-gray-300 border border-white/[0.08]',
};

interface ShopStatusBadgeProps {
  trustLabel: ShopTrustLabel;
  compact?: boolean;
  className?: string;
}

/** Trust / directory status badge for shop surfaces. */
export const ShopStatusBadge: React.FC<ShopStatusBadgeProps> = ({ trustLabel, compact, className = '' }) => {
  const label = LABELS[trustLabel] ?? 'Listed';
  const style = STYLES[trustLabel] ?? STYLES.listed;
  const Icon =
    trustLabel === 'verified' || trustLabel === 'community_verified'
      ? CheckBadgeIcon
      : trustLabel === 'pending_review'
        ? ClockIcon
        : trustLabel === 'rejected'
          ? XCircleIcon
          : trustLabel === 'archived'
            ? ArchiveBoxIcon
            : trustLabel === 'closed'
              ? NoSymbolIcon
              : null;

  return (
    <span className={`inline-flex items-center gap-1 shrink-0 ${compact ? 'text-xs' : 'text-sm'} font-medium ${style} ${className}`}>
      {Icon ? <Icon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden /> : null}
      {label}
    </span>
  );
};

export default ShopStatusBadge;
